import React, { useEffect, useMemo, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { PageHeader, Card } from "@/components/Primitives";
import KpiCard from "@/components/KpiCard";
import ChartBlock from "@/components/ChartBlock";
import SearchSelect from "@/components/SearchSelect";
import { Coins, Receipt, Ticket, Package, TrendUp, Warehouse, Tag, Percent, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// "2026-05-01" → "01-May-2026". Returns the raw string back if parse fails so
// the table never breaks on unexpected input.
const fmtDate = (iso) => {
  if (!iso || typeof iso !== "string") return iso || "";
  const [y, m, d] = iso.split("-").map((s) => Number(s));
  if (!y || !m || !d) return iso;
  return `${String(d).padStart(2, "0")}-${MONTH_SHORT[m - 1] || m}-${y}`;
};

// Integer-only formatter for the daily table — boutique managers asked for
// no decimals on line items so the column scans cleanly. We round to the
// nearest whole KWD; thousand-separators stay for readability.
const fmtInt = (n) => {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US").format(Math.round(Number(n)));
};

const MonthlySales = () => {
  const today = new Date();
  // `availableMonths` is the set of "YYYY-MM" strings the dataset actually has.
  // We use it to populate the Year + Month dropdowns so users only ever see
  // selectable options that will return data.
  const [availableMonths, setAvailableMonths] = useState([]);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-indexed
  // Scope filters cascade against each other via /ref endpoints.
  const [company, setCompany] = useState("");
  const [brand, setBrand] = useState("");
  const [locationCode, setLocationCode] = useState("");
  const [kpi, setKpi] = useState(null);
  const [priorKpi, setPriorKpi] = useState(null);
  const [daily, setDaily] = useState([]);
  const [brands, setBrands] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companyOpts, setCompanyOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);
  const [locationOpts, setLocationOpts] = useState([]);

  // Derive available years + per-year months from the dataset's monthly trend.
  const { years, monthsForYear } = useMemo(() => {
    const yearSet = new Set();
    const map = {};
    availableMonths.forEach((ym) => {
      const [y, m] = ym.split("-").map(Number);
      if (!y || !m) return;
      yearSet.add(y);
      if (!map[y]) map[y] = new Set();
      map[y].add(m);
    });
    return {
      years: Array.from(yearSet).sort((a, b) => b - a),
      monthsForYear: Object.fromEntries(
        Object.entries(map).map(([y, s]) => [y, Array.from(s).sort((a, b) => a - b)])
      ),
    };
  }, [availableMonths]);

  // Fetch the list of months that have data, once on mount.
  useEffect(() => {
    api.get("/analytics/trend/monthly").then((r) => {
      const rows = (r.data?.rows || []).filter((x) => (x?.net_value ?? 0) > 0);
      const ym = rows.map((x) => x.month);
      setAvailableMonths(ym);
      if (ym.length) {
        const latest = ym[ym.length - 1];
        const [ly, lm] = latest.split("-").map(Number);
        setYear(ly);
        setMonth(lm);
      }
    }).catch(() => setAvailableMonths([]));
  }, []);

  // Keep the picked month valid whenever the year switches.
  useEffect(() => {
    const list = monthsForYear[year];
    if (!list || list.length === 0) return;
    if (!list.includes(Number(month))) {
      setMonth(list[list.length - 1]);
    }
  }, [year, monthsForYear, month]);

  const range = useMemo(() => {
    const y = Number(year);
    const m = Number(month);
    const start = `${y}-${String(m).padStart(2, "0")}-01`;
    const last = new Date(y, m, 0);
    const end = `${y}-${String(m).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
    return { start, end };
  }, [year, month]);

  const priorRange = useMemo(() => ({
    start: range.start.replace(/^\d{4}/, String(Number(year) - 1)),
    end:   range.end.replace(/^\d{4}/, String(Number(year) - 1)),
  }), [range, year]);

  // Cascading option lists for the 3 scope filters.
  useEffect(() => {
    const ctrl = new AbortController();
    const params = (exclude) => {
      const qp = new URLSearchParams();
      if (exclude !== "company" && company) qp.append("company", company);
      if (exclude !== "brand" && brand) qp.append("brand", brand);
      if (exclude !== "location_code" && locationCode) qp.append("location_code", locationCode);
      const s = qp.toString();
      return s ? `?${s}` : "";
    };
    api.get(`/ref/companies${params("company")}`, { signal: ctrl.signal }).then((r) => setCompanyOpts(r.data.rows || [])).catch(() => {});
    api.get(`/ref/brands${params("brand")}`,       { signal: ctrl.signal }).then((r) => setBrandOpts(r.data.rows || [])).catch(() => {});
    api.get(`/ref/locations${params("location_code")}`, { signal: ctrl.signal }).then((r) => setLocationOpts(r.data.rows || [])).catch(() => {});
    return () => ctrl.abort();
  }, [company, brand, locationCode]);

  useEffect(() => {
    const scope = (start, end) => {
      const q = new URLSearchParams({ start_date: start, end_date: end });
      if (company) q.append("company", company);
      if (brand) q.append("brand", brand);
      if (locationCode) q.append("location_code", locationCode);
      return q;
    };
    const p = scope(range.start, range.end);
    const pp = scope(priorRange.start, priorRange.end);
    api.get(`/analytics/kpis?${p.toString()}`).then((r) => setKpi(r.data)).catch(() => setKpi(null));
    api.get(`/analytics/kpis?${pp.toString()}`).then((r) => setPriorKpi(r.data)).catch(() => setPriorKpi(null));
    api.get(`/analytics/trend/daily?${p.toString()}`).then((r) => setDaily(r.data.rows || []));
    api.get(`/analytics/brands?${p.toString()}`).then((r) => setBrands(r.data.rows || []));
    api.get(`/analytics/locations?${p.toString()}`).then((r) => setLocations(r.data.rows || []));
  }, [range, priorRange, company, brand, locationCode]);

  // Hits the focused backend endpoint that builds a single-sheet .xlsx with
  // exactly the rows the user sees on screen (scope-aware).
  const downloadExcel = async () => {
    if (!daily.length) { toast.error("Nothing to export — pick a month with sales."); return; }
    try {
      const q = new URLSearchParams({ start_date: range.start, end_date: range.end });
      if (company) q.append("company", company);
      if (brand) q.append("brand", brand);
      if (locationCode) q.append("location_code", locationCode);
      const res = await api.get(`/export/monthly-daily-excel?${q.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `monthly_sales_${range.start}_${range.end}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Excel export failed");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Performance · Monthly"
        title="Monthly sales report"
        subtitle="Drop in, pick any month, see exactly how that month ran — KPIs with prior-year deltas, daily breakdown, and top brand/boutique mix. All KWD."
      />

      <Card testId="monthly-picker" title="Pick the month" right={
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          {range.start} → {range.end} · vs <span className="text-myb-500 font-bold">{priorRange.start} → {priorRange.end}</span>
        </span>
      }>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-1">Year</div>
            <select
              data-testid="monthly-year-select"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              disabled={years.length === 0}
              className="border hairline bg-white px-3 py-2 rounded-sm text-sm w-[120px] disabled:opacity-50"
            >
              {years.length === 0 ? (
                <option value="">No data</option>
              ) : years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-1">Month</div>
            <select
              data-testid="monthly-month-select"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              disabled={!monthsForYear[year]?.length}
              className="border hairline bg-white px-3 py-2 rounded-sm text-sm w-[160px] disabled:opacity-50"
            >
              {(monthsForYear[year] || []).map((mm) => (
                <option key={mm} value={mm}>{MONTH_NAMES[mm - 1]}</option>
              ))}
              {!monthsForYear[year]?.length && <option value="">No data</option>}
            </select>
          </div>
        </div>

        {/* Scope row — Company / Brand / Showroom. Cascading SearchSelects
            so picking Cartier narrows showrooms to only those that sold it. */}
        <div className="flex flex-wrap gap-3 items-end mt-4 pt-4 border-t hairline">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-1">Company</div>
            <SearchSelect
              testId="monthly-filter-company"
              width="w-[200px]"
              placeholder="Company"
              allLabel="All companies"
              value={company}
              onChange={setCompany}
              options={companyOpts.map((c) => ({ value: String(c.code), label: c.name, sub: c.code }))}
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-1">Brand</div>
            <SearchSelect
              testId="monthly-filter-brand"
              width="w-[200px]"
              placeholder="Brand"
              allLabel="All brands"
              value={brand}
              onChange={setBrand}
              options={brandOpts.map((b) => ({ value: b.name, label: b.name }))}
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold mb-1">Showroom</div>
            <SearchSelect
              testId="monthly-filter-showroom"
              width="w-[240px]"
              placeholder="Showroom"
              allLabel="All showrooms"
              value={locationCode}
              onChange={setLocationCode}
              options={locationOpts.map((l) => ({ value: String(l.code), label: l.name || l.code, sub: l.code }))}
            />
          </div>
          {(company || brand || locationCode) && (
            <button
              data-testid="monthly-filter-clear"
              onClick={() => { setCompany(""); setBrand(""); setLocationCode(""); }}
              className="text-[11px] uppercase tracking-wider text-myb-500 hover:text-myb-600 font-semibold underline-offset-2 hover:underline px-2 py-2"
            >
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Numbers at a glance — same 9 KPI cards as the Dashboard, deltas
          computed by the backend against the same-month-prior-year window. */}
      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.28em] text-myb-500 font-semibold">Numbers at a glance</span>
          {kpi?.prior_year_sales != null && (
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">
              vs same month {year - 1} · <span className="tabular-nums text-myb-500 font-semibold">{fmtNumber(kpi.prior_year_sales)} KWD</span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="monthly-kpi-section">
          <KpiCard testId="monthly-kpi-total-sales"   label="Total Sales"   value={fmtNumber(kpi?.total_sales)}  suffix="KWD"      delta={kpi?.growth_pct}     icon={Coins} />
          <KpiCard testId="monthly-kpi-transactions"  label="Transactions"  value={fmtNumber(kpi?.transactions)}                   delta={kpi?.txn_growth_pct} icon={Receipt} />
          <KpiCard testId="monthly-kpi-total-qty"     label="Total Sale Qty" value={fmtNumber(kpi?.quantity)}                       delta={kpi?.qty_growth_pct} icon={Package} />
          <KpiCard testId="monthly-kpi-avg-ticket"    label="Avg Ticket"    value={fmtNumber(kpi?.avg_ticket)}   suffix="KWD"                                  icon={Ticket} />
          <KpiCard testId="monthly-kpi-avg-qty"       label="Avg Qty / Bill" value={fmtNumber(kpi?.avg_qty)}                                                   icon={Package} />
          <KpiCard testId="monthly-kpi-gross-profit"  label="Gross Profit"  value={fmtNumber(kpi?.gross_profit)} suffix="KWD"                                  icon={TrendUp} />
          <KpiCard testId="monthly-kpi-gp-margin"     label="GP Margin"     value={(kpi?.gp_margin_pct ?? 0).toFixed(1)}      suffix="%"                       icon={Warehouse} />
          <KpiCard testId="monthly-kpi-discount"      label="Discount"      value={fmtNumber(kpi?.discount)}     suffix="KWD"                                  icon={Tag} />
          <KpiCard testId="monthly-kpi-discount-pct"  label="Discount %"    value={(kpi?.discount_pct ?? 0).toFixed(1)}       suffix="of gross"                icon={Percent} />
        </div>
        <span className="hidden">{priorKpi?.total_sales}</span>
      </div>

      <Card testId="monthly-daily-table" title="Daily sales · selected month" className="mt-6"
            right={(
              <div className="flex items-center gap-3">
                {daily.length > 0 && (
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    {daily.length} day{daily.length === 1 ? "" : "s"} ·
                    <span className="text-myb-500 font-bold ml-1">
                      {fmtInt(daily.reduce((a, r) => a + (r.net_value || 0), 0))} KWD total
                    </span>
                  </span>
                )}
                <button
                  data-testid="monthly-export-excel"
                  onClick={downloadExcel}
                  disabled={daily.length === 0}
                  className="text-xs bg-myb-500 hover:bg-myb-600 text-white px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5 font-semibold disabled:opacity-50"
                >
                  <DownloadSimple size={14} weight="bold" /> Export Excel
                </button>
              </div>
            )}>
        {daily.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground italic">No transactions in {range.start.slice(0, 7)}.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-base">
              <thead>
                <tr className="text-left border-b hairline">
                  <th className="py-3 px-4 text-xs uppercase tracking-[0.2em] text-myb-500 font-semibold">Date</th>
                  <th className="py-3 px-4 text-xs uppercase tracking-[0.2em] text-myb-500 font-semibold text-right">Sale (KWD)</th>
                </tr>
              </thead>
              <tbody data-testid="monthly-daily-tbody">
                {daily.map((r) => (
                  <tr key={r.date} className="border-b hairline hover:bg-myb-50/50">
                    <td className="py-3 px-4 text-base text-foreground">{fmtDate(r.date)}</td>
                    <td className="py-3 px-4 text-right tabular-nums font-medium text-base">{fmtInt(r.net_value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t hairline bg-myb-50/60">
                  <td className="py-3 px-4 text-xs uppercase tracking-wider text-myb-500 font-semibold">Total</td>
                  <td className="py-3 px-4 text-right tabular-nums font-bold text-myb-500 text-lg">
                    {fmtInt(daily.reduce((a, r) => a + (r.net_value || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card testId="monthly-brands" title="Top brands · selected month">
          <ChartBlock spec={{ kind: "bar", x_key: "brand", y_keys: ["net_value"] }} data={(brands || []).slice(0, 10)} height={320} />
        </Card>
        <Card testId="monthly-locations" title="Top boutiques · selected month">
          <ChartBlock spec={{ kind: "bar", x_key: "location_name", y_keys: ["net_value"] }} data={(locations || []).slice(0, 10)} height={320} />
        </Card>
      </div>
    </div>
  );
};

export default MonthlySales;
